import { createTheme } from "@mui/material"

/**
 * Tokens de estilo das tabelas. Apontam para as variáveis do tema Skote para que a
 * tabela acompanhe o restante da aplicação.
 */
export const tableTokens = {
  fontFamily: "var(--bs-body-font-family)",
  fontSizeUi: "11px",
  fontSizeCell: "13px",
  colorCell: "var(--bs-table-color)",
  colorMuted: "var(--bs-gray-600)",
  stripeBg: "var(--bs-gray-100)"
}

// Tipografia da moldura da tabela: cabeçalho, toolbar, paginação e rodapé.
const uiText = {
  fontSize: tableTokens.fontSizeUi,
  fontFamily: tableTokens.fontFamily
}

// Campos de filtro do MUI trazem estilo próprio com especificidade alta.
const inputText = {
  color: `${tableTokens.colorCell} !important`,
  fontSize: `${tableTokens.fontSizeUi} !important`,
  fontWeight: "400 !important",
  fontFamily: `${tableTokens.fontFamily} !important`
}

/**
 * Tema MUI aplicado aos componentes internos do MaterialReactTable (ícones, inputs de
 * filtro, menus, paginação). Criado uma única vez no carregamento do módulo.
 */
export const tableTheme = createTheme({
  components: {
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          height: "18px",
          width: "18px"
        }
      }
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          color: tableTokens.colorCell,
          fontWeight: "400",
          ...uiText
        }
      }
    },
    MuiInput: {
      styleOverrides: {
        root: inputText
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontWeight: "600",
          ...uiText
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: inputText,
        input: {
          padding: "0.47rem 0.75rem !important"
        }
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          paddingBottom: "0px !important",
          paddingTop: "0px !important",
          ...uiText
        }
      }
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          marginRight: "0px !important"
        }
      }
    },
    MuiToolbar: {
      styleOverrides: {
        root: uiText
      }
    },
    MuiTablePagination: {
      styleOverrides: {
        selectLabel: uiText,
        displayedRows: uiText
      }
    },
    MuiSelect: {
      styleOverrides: {
        select: uiText
      }
    }
  }
})

/**
 * Props visuais aplicados a toda tabela. Consumidos por base-data-table; quem chama o
 * componente pode sobrescrever qualquer um deles.
 */
export const baseTableProps = {
  enableGlobalFilter: false,
  enableDensityToggle: false,
  enableColumnResizing: true,
  positionPagination: "bottom",
  initialState: {
    density: "compact"
  },
  displayColumnDefOptions: {
    "mrt-row-actions": {
      muiTableHeadCellProps: { align: "center" },
      size: 80,
      maxSize: 120
    }
  },
  muiTableProps: {
    sx: {
      tableLayout: "fixed"
    }
  },
  defaultColumn: {
    maxSize: 400,
    minSize: 120,
    size: 200
  },
  muiTopToolbarProps: {
    sx: {
      fontWeight: "600",
      ...uiText
    }
  },
  muiTableHeadCellProps: {
    sx: {
      fontWeight: "600",
      ...uiText
    }
  },
  muiTableBodyCellProps: {
    sx: {
      color: tableTokens.colorCell,
      fontSize: tableTokens.fontSizeCell,
      fontWeight: "400",
      fontFamily: tableTokens.fontFamily
    }
  },
  muiTableBodyProps: {
    sx: uiText
  },
  // O zebrado usa o índice da linha, não a posição no DOM: quando há renderDetailPanel o
  // MRT emite um <tr> extra por linha e um seletor :nth-of-type pintaria todas de cinza.
  muiTableBodyRowProps: ({ isDetailPanel, staticRowIndex }) => ({
    sx: {
      backgroundColor: !isDetailPanel && staticRowIndex % 2 === 0
        ? tableTokens.stripeBg
        : undefined
    }
  }),
  muiTablePaginationProps: {
    sx: {
      color: tableTokens.colorMuted,
      fontWeight: "400",
      ...uiText
    }
  },
  muiTableFooterCellProps: {
    sx: {
      fontWeight: "600",
      ...uiText
    }
  }
}
